import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {Users} from '../../icons';
import {Spinner} from '../../Spinner';
import {RolePill} from '../../elements/RolePill';
import {relativeTimeLabel} from './panelFormatters';
import './TeamCard.scss';

// Las capacidades del portal, en el orden en que las muestra su propia pantalla. El nombre de cada
// una sale de las keys de ESA pantalla (`portal_modules.*_name`): un módulo es un concepto del
// producto y no puede llamarse distinto según dónde se lo mire. Ojo: el flag es `connect` y el
// nombre visible es "Asistente (MCP)" — no coinciden, y ese desfase es el motivo de esta tabla.
//
// **`memory` NO está acá a propósito.** `AgencyClient._portal_default_memory` viene con
// `default=True`, así que el chip aparecería para todo el mundo; pero la franja que lo gobierna en
// `/client/:id/portal-config` es SuperAdmin-only y está rotulada "interno" (`isSuperAdmin && ...`
// en `portal_modules/PortalModulesGrid.js`). Para un admin de agencia común eso es un acceso
// anunciado cuyo "Gestionar →" lleva a una pantalla donde no existe — y de paso le muestra el
// nombre de un feature interno. Cuando la franja salga del dogfood y se abra a toda agencia, esta
// línea vuelve: `['memory', 'portal_modules.module_memory_name']`.
const PORTAL_FEATURES = [
    ['dashboard', 'portal_modules.module_dashboard_name'],
    ['reports', 'portal_modules.module_reports_name'],
    ['connect', 'portal_modules.module_mcp_name'],
    ['connections', 'portal_modules.module_connections_name'],
];

// Las tres columnas de actividad. Estas MISMAS keys alimentan el encabezado de la tabla y el
// `data-label` de cada celda, que es lo que el CSS muestra como etiqueta inline cuando la card es
// angosta y la fila se apila: una columna no puede llamarse distinto según el ancho de la pantalla.
// Sin la etiqueta, el fallback dejaba tres valores huérfanos ("hace 26 días", "—", "—") sin nada que
// dijera qué era cada uno, porque el apilado oculta el encabezado.
const ACTIVITY_COLUMNS = {
    lastSeen: ['client_panel.team_col_last_seen', 'Último acceso'],
    mcp: ['client_panel.team_col_mcp', 'Uso de la IA'],
    log: ['client_panel.team_col_log', 'Bitácora'],
};

const columnLabel = (t, column) => t(...ACTIVITY_COLUMNS[column]);

/**
 * Sección "Usuarios" del Panel (cards abUohYUd + 4upX4kqS): quién llega a este cliente, en dos
 * bloques separados.
 *
 * **Por qué separados y no una lista con chip de rol** (que es lo que proponía el mockup): "quién de
 * mi equipo atiende a este cliente" y "a quién del cliente le di acceso" son dos preguntas
 * distintas, y mezclarlas obliga a leer el chip de cada fila para saber cuál se está contestando.
 *
 * **La actividad no se pide, se recibe.** Las columnas de uso las ve sólo el admin de la agencia, y
 * eso lo decide el BACKEND: omite `activity` para los demás. Acá se renderiza por PRESENCIA del
 * dato — el frontend no tiene forma honesta de saber el rol de quien mira (`LocalStorage.getUserType`
 * es otra dimensión: agencia/cliente/CRM, no el rol dentro de la agencia).
 *
 * **Los accesos del portal son de sólo lectura**, con link a su pantalla. Los gobierna
 * `AgencyClient._portal_default_*` y los usuarios los heredan en vivo: editarlos también desde acá
 * garantiza que una de las dos superficies mienta.
 *
 * No reemplaza a la `PortalCard` del rail: aquélla cuenta si el cliente entró y qué uso le da; ésta,
 * quién tiene la llave. Conviven a propósito.
 */
export function TeamCard({data, portal, loading, clientId}) {
    const {t} = useTranslation();

    if (loading) {
        return (
            <div className="box pnl-card">
                <div className="box-loader"><Spinner color="positive small"/></div>
            </div>
        );
    }
    // El shell ya gatea por la presencia del bloque; el guard sostiene el montaje directo desde
    // tests y la ventana en que el backend todavía no lo manda.
    if (!data) return null;

    const members = data.users || [];

    return (
        <div className="box pnl-card">
            <div className="pnl-card__head">
                <h2 className="pnl-card__title">{t('client_panel.team_title', 'Usuarios')}</h2>
            </div>

            {/* La separación entre los dos bloques la resuelve ESTE contenedor (`.pnl-team` es un
                flex column con `gap: 18px`) más el divisor explícito, así que las dos <section>
                van sin clase: el `pnl-team__block` que llevaban no tenía —ni necesitaba— una sola
                regla en todo el bundle, y dársela ahora duplicaría el aire que ya está acá. */}
            <div className="pnl-cnt pnl-team">
                <AgencyBlock members={members} clientId={clientId} t={t}/>
                {/* El divisor separa DOS bloques: sin el de portal —que no se monta cuando el
                    backend no manda ese bloque— quedaba una línea horizontal colgando al pie de la
                    card, separando el equipo de nada. */}
                {portal && <div className="pnl-team__divider"/>}
                <PortalBlock portal={portal} clientId={clientId} t={t}/>
            </div>
        </div>
    );
}

/** Bloque 1: el equipo de la agencia, con su actividad si quien mira puede verla. */
function AgencyBlock({members, clientId, t}) {
    // Por columna y no por bloque: cada una aparece si ALGÚN miembro trae su dato. Así el frontend
    // obedece lo que el backend mande sin decidir nada por su cuenta — si mañana viaja el último
    // acceso para todos y el uso sólo para admins, esto ya funciona.
    const showActivity = members.some((member) => member.activity);

    return (
        <section>
            <header className="pnl-team__subhead">
                <h3>{t('client_panel.team_agency_title', 'Equipo de la agencia')}</h3>
                {members.length > 0 && <span className="pnl-cnt">({members.length})</span>}
            </header>

            {members.length === 0 ? (
                <div className="pnl-empty">
                    <Users size={30}/>
                    <p>
                        {t('client_panel.team_agency_empty',
                            'Nadie de tu equipo tiene asignado este cliente.')}
                    </p>
                    {/* A la pantalla que asigna usuarios a ESTE cliente, no a `/agency-team` (que
                        administra los usuarios de toda la agencia y sus roles): el vacío que hay
                        que llenar es la asignación. Además en 2.1 ese destino es la subnav de
                        Configuración, así que el usuario no se va del cliente. */}
                    <Link className="pnl-btn pnl-btn--sm" to={`/client/${clientId}/dashbo-users`}>
                        {t('client_panel.team_assign', 'Asignar usuarios')} &rarr;
                    </Link>
                </div>
            ) : (
                <div className={`pnl-team__table${showActivity ? ' -with-activity' : ''}`}>
                    <div className="pnl-team__row -head">
                        <span>{t('client_panel.team_col_user', 'Usuario')}</span>
                        {showActivity && (
                            <>
                                <span>{columnLabel(t, 'lastSeen')}</span>
                                <span>{columnLabel(t, 'mcp')}</span>
                                <span>{columnLabel(t, 'log')}</span>
                            </>
                        )}
                    </div>
                    {members.map((member) => (
                        <MemberRow key={member.id} member={member} showActivity={showActivity} t={t}/>
                    ))}
                </div>
            )}
        </section>
    );
}

function MemberRow({member, showActivity, t}) {
    const activity = member.activity;

    return (
        <div className="pnl-team__row">
            <span className="pnl-team__who">
                <span className="pnl-team__name">
                    {/* El nombre va envuelto para poder truncarlo: es lo ÚNICO de la fila que puede
                        ceder ancho (la pastilla de rol y el hint son cortos y fijos). Sin este
                        wrapper el texto no comprime, desborda su celda y se monta sobre la primera
                        columna de actividad — que era el motivo real por el que la tabla se apilaba
                        de más. */}
                    <span className="pnl-team__label">{member.name}</span>
                    <RolePill userType={member.role}/>
                    {/* Quién ATIENDE al cliente y quién simplemente PUEDE entrar son dos cosas
                        distintas, y sin esta marca se ven igual: un Admin llega a todos los
                        clientes de su agencia por su rol, sin estar asignado a ninguno. Va sobre
                        los no asignados —que son los muchos— y no sobre los asignados, para que la
                        lista no se llene de pastillas. El backend ya los manda al final.
                        `is_assigned === false` explícito: con un backend viejo el campo no viene y
                        no se marca a nadie, en vez de marcarlos a todos. */}
                    {member.is_assigned === false && (
                        <span className="pnl-team__hint">
                            {t('client_panel.team_by_role', 'acceso por rol')}
                        </span>
                    )}
                </span>
                {member.email && <span className="pnl-team__email">{member.email}</span>}
            </span>

            {showActivity && (
                <>
                    {/* "Nunca" y no un guion: que una persona no haya entrado nunca dice algo sobre
                        ella y es accionable. El guion queda para los contadores en cero, donde el
                        reproche sería ruido. */}
                    <span className="pnl-team__val" data-label={columnLabel(t, 'lastSeen')}>
                        {relativeTimeLabel(activity?.last_activity_at, t)
                            || t('client_panel.team_never', 'Nunca')}
                    </span>
                    <ActivityCell
                        count={activity?.mcp?.queries}
                        at={activity?.mcp?.last_used_at}
                        countKey="client_panel.team_mcp_queries"
                        singular="{{count}} consulta"
                        plural="{{count}} consultas"
                        label={columnLabel(t, 'mcp')}
                        t={t}/>
                    <ActivityCell
                        count={activity?.log?.events}
                        at={activity?.log?.last_event_at}
                        countKey="client_panel.team_log_actions"
                        singular="{{count}} acción"
                        plural="{{count}} acciones"
                        label={columnLabel(t, 'log')}
                        t={t}/>
                </>
            )}
        </div>
    );
}

/** "12 consultas · hace 2 días", o un guion si nunca pasó. */
function ActivityCell({count, at, countKey, singular, plural, label, t}) {
    const total = Number(count) || 0;
    // El guion también lleva etiqueta: apilado y sin encabezado, un "—" pelado no dice de qué es el
    // cero. Es justamente la celda que más contexto necesita.
    if (total === 0) return <span className="pnl-team__val -none" data-label={label}>—</span>;

    const when = relativeTimeLabel(at, t);
    return (
        <span className="pnl-team__val" data-label={label}>
            {/* El plural lo elige `count`, nunca otra variable: con cualquier otro nombre i18next
                se queda siempre en la forma `_other` y no hay forma de notarlo mirando las keys. */}
            <strong>{t(countKey, {count: total, defaultValue_one: singular, defaultValue_other: plural})}</strong>
            {when && <span className="pnl-team__when"> · {when}</span>}
        </span>
    );
}

/**
 * Los accesos que el portal ofrece a este cliente, de sólo lectura.
 *
 * Con todo apagado se dice explícitamente, en vez de dejar la etiqueta sola: un portal sin ninguna
 * capacidad es una configuración posible (y la explicación de por qué el cliente no ve nada), así
 * que callarlo lo haría pasar por un dato que no llegó.
 */
function PortalAccess({features, t}) {
    const enabled = PORTAL_FEATURES.filter(([flag]) => features[flag]);

    return (
        <p className="pnl-team__access">
            <span className="pnl-team__access-label">
                {t('client_panel.team_access_label', 'Accesos del portal:')}
            </span>
            {enabled.length === 0
                ? <span className="pnl-team__muted">{t('client_panel.team_access_none', 'ninguno')}</span>
                : enabled.map(([flag, labelKey]) => (
                    <span key={flag} className="pnl-team__chip">{t(labelKey)}</span>
                ))}
        </p>
    );
}

/** Bloque 2: los usuarios del portal, los invitados que todavía no entraron y sus accesos. */
function PortalBlock({portal, clientId, t}) {
    if (!portal) return null;

    const users = portal.users || [];
    // Los invitados cuentan como «ya compartiste el portal». El agregado los manda desde siempre y
    // la card los ignoraba, así que un cliente invitado producía DOS afirmaciones contradictorias
    // en la misma pantalla: la card del rail decía «Invitación enviada a x@y» y esta sección
    // «Todavía no compartiste el portal con nadie».
    const invitations = portal.pending_invitations || [];
    const features = portal.default_features;
    const configUrl = `/client/${clientId}/portal-config`;

    return (
        <section>
            <header className="pnl-team__subhead">
                <h3>{t('client_panel.team_portal_title', 'Clientes con acceso al portal')}</h3>
                {users.length > 0 && <span className="pnl-cnt">({users.length})</span>}
                <span className="pnl-card__spacer"/>
                <Link className="pnl-card__link" to={configUrl}>
                    {t('client_panel.team_manage_portal', 'Gestionar')} &rarr;
                </Link>
            </header>

            {users.length === 0 && invitations.length === 0 ? (
                <p className="pnl-team__muted">
                    {t('client_panel.team_portal_empty',
                        'Todavía no compartiste el portal con nadie del cliente.')}
                </p>
            ) : (
                <ul className="pnl-team__portal-list">
                    {users.map((user) => (
                        <li key={`user-${user.id}`}>
                            <span className="pnl-team__name">{user.name}</span>
                            {user.email && <span className="pnl-team__email">{user.email}</span>}
                            <span className="pnl-team__val">
                                {relativeTimeLabel(user.last_login_at, t)
                                    || t('client_panel.team_portal_never', 'Nunca entró')}
                            </span>
                        </li>
                    ))}
                    {/* El invitado se identifica por su email —no tiene nombre todavía— y lleva la
                        pastilla de pendiente que ya usa la pantalla de equipo, para que no se lea
                        como alguien que entra. La fecha es la del envío: es lo accionable, porque
                        dice hace cuánto que esa invitación está sin aceptar. */}
                    {invitations.map((invitation) => (
                        <li key={`invitation-${invitation.id}`}>
                            <span className="pnl-team__name">
                                {invitation.email}
                                {/* El mismo objeto que usa la pantalla de equipo para una
                                    invitación pendiente: la pastilla del rol que va a tener
                                    (cliente) más la opacidad que significa «todavía no activo».
                                    Inventar una pastilla propia acá haría que el mismo estado se
                                    vea distinto en dos pantallas. */}
                                <span className="role-pill -client team-role-pending">
                                    {t('client_panel.team_portal_invited', 'Invitación enviada')}
                                </span>
                            </span>
                            <span className="pnl-team__val">
                                {relativeTimeLabel(invitation.sent_at, t)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {/* Una sola fila para todo el cliente, no una por usuario: son los defaults que el
                portal ofrece y que cada usuario hereda en vivo. Si el backend todavía no los manda,
                la fila entera no va — mejor ausente que inventada. */}
            {features && <PortalAccess features={features} t={t}/>}
        </section>
    );
}
